import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuChevronRight, LuChevronDown, LuPencil, LuNavigation, LuWrench, LuPlus, LuMapPinOff, LuTrash2 } from 'react-icons/lu';
import api from '../../services/api';
import { estadoDe, haceCuanto } from '../../utils/pedidosEmpresa';
import ModalShell from '../ui/ModalShell';
import ConfirmDialog from '../ui/ConfirmDialog';
import MapaLeaflet, { linkGps } from '../mapa/MapaLeaflet';
import { VisitaEquipo } from './InformeTrabajo';
import AgregarEquipo from './AgregarEquipoEmpresa';

// Ficha de un cliente en el Portal Empresa (14-oct-2026): estado del service, mapa, sus equipos
// (y cargar uno nuevo ahí mismo), los trabajos que ya le hicimos y sus pedidos.
export const SEM = {
    VENCIDO:   { color: '#DC2626', label: 'Service vencido' },
    PRONTO:    { color: '#D48800', label: 'Vence en menos de 30 días' },
    AL_DIA:    { color: '#16A34A', label: 'Al día' },
    SIN_DATOS: { color: '#9CA3AF', label: 'Sin service registrado' },
};
export const ddmmaa = f => (f ? String(f).slice(0, 10).split('-').reverse().join('/').replace(/\/(\d{2})(\d{2})$/, '/$2') : '');
const ROTULO = 'text-label font-black uppercase tracking-widest text-muted';

// Trabajos hechos: una línea por equipo atendido; al tocarla se ve el detalle con fotos y repuestos
function Trabajos({ sedeId }) {
    const [lista, setLista] = useState(null);
    const [abierto, setAbierto] = useState(null);
    const [todos, setTodos] = useState(false);
    useEffect(() => { api.get(`/empresa/clientes/${sedeId}/trabajos`).then(r => setLista(Array.isArray(r.data) ? r.data : [])).catch(() => setLista([])); }, [sedeId]);
    if (lista === null) return <div className="h-14 rounded-xl bg-panel animate-pulse" />;
    if (!lista.length) return <p className="text-caption text-muted">Todavía no hay trabajos registrados.</p>;
    const visibles = todos ? lista : lista.slice(0, 6);
    return (
        <div className="space-y-1.5">
            <div className="rounded-xl bg-panel divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                {visibles.map(v => (
                    <div key={v.id}>
                        <button type="button" onClick={() => setAbierto(a => (a === v.id ? null : v.id))} className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
                            <span className="shrink-0 w-14 text-caption font-black text-ink">{ddmmaa(v.fecha)}</span>
                            <span className="flex-1 min-w-0">
                                <span className="block text-caption font-bold text-ink truncate">{v.trabajo || 'Sin detalle'}</span>
                                <span className="block text-label text-muted truncate">{[v.serie ? `N/S ${v.serie}` : null, v.tecnico, v.repuestos?.length ? `${v.repuestos.length} repuesto${v.repuestos.length !== 1 ? 's' : ''}` : null,
                                    (() => { const n = [v.fotoAntes, v.fotoDespues, ...(v.fotosExtra || []), ...(v.fotosTrabajo || [])].filter(Boolean).length; return n ? `${n} foto${n !== 1 ? 's' : ''}` : null; })()].filter(Boolean).join(' · ')}</span>
                            </span>
                            {abierto === v.id ? <LuChevronDown size={16} className="text-muted" /> : <LuChevronRight size={16} className="text-muted" />}
                        </button>
                        {abierto === v.id && <div className="px-2 pb-2"><VisitaEquipo v={v} /></div>}
                    </div>
                ))}
            </div>
            {lista.length > visibles.length && (
                <button type="button" onClick={() => setTodos(true)} className="w-full h-9 rounded-xl bg-chip text-label font-black text-secondary">Ver los {lista.length}</button>
            )}
        </div>
    );
}

export default function FichaClienteEmpresa({ cli, pedidos, puedeEditar, onCerrar, onEditar, onPedir, onAbrirPedido, onEquipo, onCambio, onBaja,
    hermanas = [], onAbrirOtra, onOtraDireccion }) {
    const [agregando, setAgregando] = useState(false);
    // Dar de baja (14-oct-2026): no se borra, sale de las listas y se restaura desde "Clientes dados de baja"
    const [confirmarBaja, setConfirmarBaja] = useState(false);
    const [dandoBaja, setDandoBaja] = useState(false);
    const darDeBaja = async () => {
        setDandoBaja(true);
        try {
            await api.delete(`/empresa/clientes/${cli.id}`);
            toast.success(`${cli.nombre} dado de baja`);
            setConfirmarBaja(false);
            onBaja && onBaja();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo dar de baja');
            setConfirmarBaja(false);
        } finally { setDandoBaja(false); }
    };
    const sem = SEM[cli.estado] || SEM.SIN_DATOS;
    const suyos = pedidos.filter(p => p.sedeId === cli.id).sort((a, b) => String(b.creadoEn).localeCompare(String(a.creadoEn))).slice(0, 8);
    return (
        <ModalShell titulo={cli.codigo ? `${cli.codigo} - ${cli.nombre}` : cli.nombre} subtitulo={cli.direccion} onCerrar={onCerrar} ancho="md:max-w-xl"
            pie={<button type="button" onClick={onPedir} className="w-full h-12 rounded-xl bg-brand-red text-white text-label font-black inline-flex items-center justify-center gap-1.5"><LuWrench size={15} /> Pedir service para este cliente</button>}>
            <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="h-7 px-2.5 rounded-full inline-flex items-center gap-1.5 text-caption font-black" style={{ background: `${sem.color}1F`, color: sem.color }}>
                        <span className="w-2 h-2 rounded-full" style={{ background: sem.color }} />{sem.label}
                    </span>
                    {cli.ultimaVisita && <span className="text-caption text-muted">Último service {ddmmaa(cli.ultimaVisita)}</span>}
                    {(cli.origen === 'LOTE' || cli.instaladoEl) && <span className="text-caption text-muted">· {cli.origen === 'LOTE' ? 'Del lote' : `Instalado ${ddmmaa(cli.instaladoEl)}`}</span>}
                    <span className="flex-1" />
                    {puedeEditar && <button type="button" onClick={onEditar} className="h-8 px-3 rounded-lg bg-chip text-label font-black text-secondary inline-flex items-center gap-1"><LuPencil size={13} /> Editar</button>}
                </div>
                {cli.notas && <p className="p-3 rounded-xl bg-panel text-caption text-secondary">{cli.notas}</p>}
                {cli.lat != null ? (
                    <div className="relative isolate rounded-2xl overflow-hidden border border-black/[0.06] dark:border-white/[0.06]">
                        <MapaLeaflet puntos={[{ id: 'c', lat: cli.lat, lng: cli.lng, color: sem.color }]} ajustarKey={cli.id} className="w-full h-40" />
                        <a href={linkGps(cli.direccion, cli.lat, cli.lng)} target="_blank" rel="noreferrer" className="absolute right-2 bottom-2 z-[500] h-9 px-3 rounded-xl bg-card shadow text-label font-black text-ink inline-flex items-center gap-1.5"><LuNavigation size={14} /> Cómo llegar</a>
                    </div>
                ) : puedeEditar && (
                    <button type="button" onClick={onEditar} className="w-full flex items-center gap-2 p-3 rounded-xl bg-panel text-left">
                        <LuMapPinOff size={16} className="shrink-0 text-muted" />
                        <span className="flex-1 text-caption text-secondary">No está marcado en el mapa. <b className="text-ink">Marcalo</b> así el técnico llega sin vueltas.</span>
                        <LuChevronRight size={16} className="text-muted" />
                    </button>
                )}

                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <p className={ROTULO}>Equipos ({cli.equipos.length})</p>
                        {!agregando && <button type="button" onClick={() => setAgregando(true)} className="h-8 px-2.5 rounded-lg bg-chip text-label font-black text-ink inline-flex items-center gap-1"><LuPlus size={14} /> Agregar equipo</button>}
                    </div>
                    {agregando && <div className="mb-2"><AgregarEquipo sedeId={cli.id} onCancelar={() => setAgregando(false)} onListo={() => { setAgregando(false); onCambio(); }} /></div>}
                    {cli.equipos.length === 0 ? (!agregando && <p className="text-caption text-muted">Todavía no tiene equipos cargados.</p>) : (
                        <div className="rounded-xl bg-panel divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                            {cli.equipos.map(e => (
                                <button key={e.id} type="button" onClick={() => onEquipo(e)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-body font-black text-ink">{e.serie}</span>
                                        {(e.modelo || e.ubicacion) && <span className="block text-caption text-muted truncate">{[e.modelo, e.ubicacion].filter(Boolean).join(' · ')}</span>}
                                    </span>
                                    <span className="text-label text-muted">Historial</span>
                                    <LuChevronRight size={16} className="text-muted" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {(hermanas.length > 0 || (puedeEditar && onOtraDireccion)) && (
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <p className={ROTULO}>{hermanas.length ? `Otras direcciones (${hermanas.length})` : 'Otras direcciones'}</p>
                            {puedeEditar && onOtraDireccion && <button type="button" onClick={onOtraDireccion} className="h-8 px-2.5 rounded-lg bg-chip text-label font-black text-ink inline-flex items-center gap-1"><LuPlus size={14} /> Agregar otra dirección</button>}
                        </div>
                        {hermanas.length === 0 ? <p className="text-caption text-muted">Si este cliente tiene equipos en otro lugar, agregale esa dirección.</p> : (
                            <div className="rounded-xl bg-panel divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                                {hermanas.map(h => (
                                    <button key={h.id} type="button" onClick={() => onAbrirOtra && onAbrirOtra(h)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
                                        <span className="flex-1 min-w-0 text-caption font-bold text-ink truncate">{h.direccion || 'Sin dirección'}</span>
                                        <span className="text-label text-muted">{h.equipos.length} eq.</span>
                                        <LuChevronRight size={16} className="text-muted" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div>
                    <p className={`${ROTULO} mb-1.5`}>Trabajos hechos</p>
                    <Trabajos sedeId={cli.id} />
                </div>

                {suyos.length > 0 && (
                    <div>
                        <p className={`${ROTULO} mb-1.5`}>Pedidos</p>
                        <div className="rounded-xl bg-panel divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                            {suyos.map(p => {
                                const est = estadoDe(p);
                                return (
                                    <button key={p.id} type="button" onClick={() => onAbrirPedido(p)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-caption font-bold text-ink truncate">{p.motivo}{p.equipoSerie ? ` · N/S ${p.equipoSerie}` : ''}</span>
                                            <span className="block text-label text-muted">#{p.id} · {haceCuanto(p.creadoEn)}</span>
                                        </span>
                                        <span className="shrink-0 text-label font-black" style={{ color: est.color }}>{est.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
                {puedeEditar && onBaja && (
                    <button type="button" onClick={() => setConfirmarBaja(true)}
                        className="w-full h-10 rounded-xl text-label font-black text-muted inline-flex items-center justify-center gap-1.5 active:bg-chip">
                        <LuTrash2 size={14} /> Dar de baja este cliente
                    </button>
                )}
            </div>
            {confirmarBaja && (
                <ConfirmDialog titulo={`¿Dar de baja a ${cli.nombre}?`}
                    mensaje="Deja de aparecer en tu lista, en Services y en los pedidos. Su historial no se borra y lo podés restaurar desde «Clientes dados de baja»."
                    textoConfirmar={dandoBaja ? 'Dando de baja…' : 'Sí, dar de baja'} textoCancelar="Volver"
                    onCancelar={() => setConfirmarBaja(false)} onConfirmar={darDeBaja} />
            )}
        </ModalShell>
    );
}
